import {Alert, AlertTitle, Box, Grid} from "@mui/material";
import {
	BreadcrumbItem,
	BreadcrumbLink,
	Breadcrumbs,
	Checkbox,
	ConfirmButton,
	defined,
	EditForm,
	TextField,
	View,
	ViewHeader,
} from "@variocube/app-ui";
import React, {useEffect, useState} from "react";
import {useAsync} from "react-async-hook";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import {useCreditCardApi} from "../../api";
import {CreditorSelect} from "../../component/creditor-select";
import {useLocalization} from "../../i18n";
import {useNumericParam} from "../../utils/useParam";

export function CreditCardEdit() {
	const {s, t} = useLocalization();
	const {getCreditCard, updateCreditCard, deleteCreditCard} = useCreditCardApi();
	const navigate = useNavigate();
	const id = useNumericParam("id");

	const [cardNumber, setCardNumber] = useState("");
	const [creditorId, setCreditorId] = useState<number | undefined>();
	const [active, setActive] = useState(true);

	const {loading, error, result} = useAsync(getCreditCard, [id]);

	async function handleDelete() {
		await deleteCreditCard(id);
		navigate("../..", {relative: "path"});
	}

	useEffect(() => {
		if (result) {
			setCardNumber(result.cardNumber);
			setCreditorId(result.creditor.id ?? undefined);
			setActive(result.active);
		}
	}, [result]);

	async function handleSave() {
		if (defined(cardNumber) && defined(creditorId)) {
			await updateCreditCard(id, {
				cardNumber,
				creditorId,
				active,
			});
			navigate(`..`, {relative: "path"});
		}
	}

	return (
		<View error={error}>
			<Breadcrumbs>
				<BreadcrumbLink component={RouterLink} to=".." relative="path">
					{t("navigation.adminGroup.creditCards")}
				</BreadcrumbLink>
				<BreadcrumbItem>{t("creditCards.edit.title")}</BreadcrumbItem>
			</Breadcrumbs>
			<ViewHeader title={t("creditCards.edit.title")} />
			<EditForm loading={loading} onSave={handleSave} labels={s("actions")}>
				<Box p={2}>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							<TextField
								label={t("creditCards.cardNumber")}
								fullWidth
								required
								value={cardNumber}
								onChange={setCardNumber}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<CreditorSelect
								label={t("creditCards.creditCardCompany")}
								value={creditorId}
								onChange={setCreditorId}
								required
								creditCardProvider
							/>
						</Grid>
						<Grid item xs={12}>
							<Checkbox label={t("creditCards.active")} checked={active} onChange={setActive} />
						</Grid>
					</Grid>
				</Box>
			</EditForm>
			<Alert
				severity="error"
				action={
					<ConfirmButton
						color="error"
						onConfirm={handleDelete}
						title={t("creditCards.delete.title")}
						cancel={t("actions.cancel")}
					>
						{t("creditCards.delete.message")}
					</ConfirmButton>
				}
			>
				<AlertTitle>{t("creditCards.delete.title")}</AlertTitle>
				{t("creditCards.delete.info")}
			</Alert>
		</View>
	);
}
